/**
 * @architecture-header 1
 * @layer module
 * @domain reports
 * @responsibility Summarise one assignment and state, by rule, what it cannot be used to conclude.
 * @state-owner none; callers pass dataset records in
 * @allowed-imports shared view types, demo constants and selectors
 * @forbidden React; network; store implementations
 * @public-api reports activity insight dialog
 * @side-effects none
 * @tests modules/reports/model/run-report.test.ts
 */

import { MISCONCEPTIONS, QUESTIONS } from '../../../shared/lib/demo-data';
import type { AnswerMark, ClassRecord, RunRecord, SeatRecord } from '../../../shared/lib/model';
import type { AnswerSheet, RunReportState } from '../../../shared/model/dataset-store';
import { answerKey } from '../../../shared/model/dataset-store';

function passed(mark: AnswerMark): boolean {
  return mark === 'ok' || mark === 'fix';
}

/** Below this many questions a per-concept score says more about luck than about mastery. */
export const SUBSCORE_MINIMUM = 20;
/** A wrong route only counts as a pattern once it shows up on more than one question. */
export const MISCONCEPTION_MINIMUM_ITEMS = 2;

export interface StudentRow {
  readonly name: string;
  readonly answered: number;
  readonly correct: number;
  /** The concept behind their first wrong answer, or null when nothing went wrong. */
  readonly topConcern: string | null;
}

export interface ItemRow {
  readonly index: number;
  readonly stem: string;
  readonly concept: string;
  readonly incorrect: number;
  readonly total: number;
  readonly students: readonly string[];
}

export interface MisconceptionRow {
  readonly id: string;
  readonly label: string;
  readonly items: number;
  readonly students: readonly string[];
}

export interface Caveat {
  readonly id: string;
  readonly text: string;
  /**
   * Shown in the footer. Only coverage and the single strongest "cannot conclude" earn this:
   * six lines of red read as "the whole report is unreliable", which is not what they mean.
   */
  readonly primary: boolean;
}

export interface RunReport {
  readonly assigned: number;
  readonly started: number;
  readonly submitted: number;
  readonly complete: boolean;
  readonly questions: number;
  readonly topIssue: { readonly label: string; readonly affected: number } | null;
  /** Plain-language limits, generated by rule rather than written by hand. */
  readonly caveats: readonly Caveat[];
  readonly students: readonly StudentRow[];
  readonly items: readonly ItemRow[];
  readonly misconceptions: readonly MisconceptionRow[];
  readonly pendingVerdicts: number;
  readonly unreviewedStudents: number;
}

export interface RunReportInput {
  readonly run: RunRecord;
  readonly classes: readonly ClassRecord[];
  readonly answers: Readonly<Record<string, AnswerSheet>>;
  readonly seats: Readonly<Record<number, readonly SeatRecord[]>>;
  readonly report: RunReportState;
}

export function runReport(input: RunReportInput): RunReport {
  const { answers, classes, report, run, seats } = input;
  const roster = classes[run.classIndex]?.students ?? [];
  const audience = run.audience === 'some' && run.students?.length ? run.students : roster;
  const classSeats = seats[run.classIndex] ?? [];
  const complete = run.status === 'ended';

  const sheets = audience
    .map((name) => ({ name, sheet: answers[answerKey(run.id, name)] }))
    .filter((entry): entry is { name: string; sheet: AnswerSheet } => Boolean(entry.sheet));

  const students: StudentRow[] = sheets.map(({ name, sheet }) => {
    const firstWrong = sheet.marks.findIndex((mark) => !passed(mark));
    return {
      name,
      answered: sheet.marks.length,
      correct: sheet.marks.filter(passed).length,
      topConcern: firstWrong >= 0 ? (QUESTIONS[firstWrong]?.concept ?? null) : null,
    };
  });

  const items: ItemRow[] = QUESTIONS.map((question, index) => {
    const wrong = sheets
      .filter(({ sheet }) => sheet.marks[index] && !passed(sheet.marks[index]))
      .map(({ name }) => name);
    return {
      index,
      stem: question.text,
      concept: question.concept,
      incorrect: wrong.length,
      total: sheets.length,
      students: wrong,
    };
  });

  /** Only patterns that span more than one question; a single item cannot establish a route. */
  const misconceptions: MisconceptionRow[] = MISCONCEPTIONS.map((misconception) => {
    const indexes = QUESTIONS.map((question, index) => ({ question, index })).filter(
      (entry) => entry.question.misconceptionId === misconception.id,
    );
    const affected = new Set(indexes.flatMap((entry) => items[entry.index].students));
    return {
      id: misconception.id,
      label: misconception.label,
      items: indexes.length,
      students: [...affected],
    };
  })
    .filter((row) => row.items >= MISCONCEPTION_MINIMUM_ITEMS && row.students.length > 0)
    .sort((a, b) => b.students.length - a.students.length);

  const worst = [...items].sort((a, b) => b.incorrect - a.incorrect)[0];
  const topIssue =
    worst && worst.incorrect > 0 ? { label: worst.stem, affected: worst.incorrect } : null;

  const submitted = complete ? audience.length : 0;
  const started = complete
    ? audience.length
    : classSeats.filter((seat) => audience.includes(seat.name) && seat.status !== 'offline').length;

  const pendingVerdicts = sheets.reduce(
    (sum, { sheet }) => sum + sheet.marks.filter((mark) => mark === 'low').length,
    0,
  );
  const unreviewedStudents = sheets.filter(({ name, sheet }) => {
    const seen = sheet.reviewed || !sheet.marks.some((mark) => mark === 'low');
    return !seen && !report.approved[name];
  }).length;

  /**
   * One sentence per triggered rule, in plain language. Statistical wording is deliberately
   * avoided: a caveat nobody can read is a caveat nobody acts on. Only two reach the footer:
   * who the data covers, and the one conclusion it cannot support. The rest live in the guide,
   * because a wall of red warnings discredits the whole report instead of qualifying it.
   */
  const conceptsCovered = new Set(QUESTIONS.map((question) => question.concept)).size;
  const perConcept = Math.max(1, Math.round(QUESTIONS.length / conceptsCovered));
  const caveats: Caveat[] = [];
  if (!complete) {
    caveats.push({
      id: 'coverage',
      text: `本表基於 ${sheets.length} 份未完成作答，不含已交出卷。`,
      primary: true,
    });
  }
  if (QUESTIONS.length < SUBSCORE_MINIMUM) {
    caveats.push({
      id: 'thin-concepts',
      text: `每個概念在這次評量只有 ${perConcept} 題，不足以判斷概念掌握。`,
      primary: true,
    });
  }
  if (sheets.length < SUBSCORE_MINIMUM) {
    caveats.push({
      id: 'small-n',
      text: `只有 ${sheets.length} 人的作答，單題比例容易上下跳動。`,
      primary: false,
    });
  }
  if (pendingVerdicts) {
    caveats.push({
      id: 'pending',
      text: `還有 ${pendingVerdicts} 題是待確認的判定，確認後數字會變。`,
      primary: false,
    });
  }
  if (!misconceptions.length) {
    caveats.push({
      id: 'no-routes',
      text: '每個錯法只出現在一題，不足以聚合成錯誤類型。',
      primary: false,
    });
  }
  caveats.push({
    id: 'cross-paper',
    text: '跨評量的概念趨勢請看「下一堂課」。',
    primary: false,
  });

  return {
    assigned: audience.length,
    started,
    submitted,
    complete,
    questions: QUESTIONS.length,
    topIssue,
    caveats,
    students,
    items,
    misconceptions,
    pendingVerdicts,
    unreviewedStudents,
  };
}

/** The three things the opening paragraph has to answer: what, where it stands, what to look at. */
export function runReportSummary(report: RunReport, activityName: string): readonly string[] {
  return [
    `${activityName}，${report.questions} 題，指派 ${report.assigned} 人。`,
    report.complete
      ? `${report.submitted} 人已交，全部收齊。`
      : `目前 ${report.started} 人作答中、${report.submitted} 人已交。尚未收齊，以下為進行中快照。`,
    report.topIssue
      ? `最多人卡住的是「${report.topIssue.label}」，${report.topIssue.affected} 人答錯。`
      : '目前沒有題目有人答錯。',
  ];
}
